<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import type { Geofence, PlaceSuggestion } from '@gpx-spa/geo-domain';
import { createGoogleMapsJsPlacesProvider } from '@gpx-spa/geo-adapters';
import { PlaceAutocomplete, usePlaceAutocomplete } from '@gpx-spa/ui';
import AutoComplete from 'primevue/autocomplete';
import IconField from 'primevue/iconfield';
import InputIcon from 'primevue/inputicon';
import type { ShipmentFormStopRow } from '@/stores/useShipmentFormStore';
import { useShipmentFormStore } from '@/stores/useShipmentFormStore';
import type { ShipmentFormStopFieldErrors } from '@/components/shipment/form/utils/shipment-form-stop-validation';
import { getStopRoutingCoordinates } from '@/components/shipment/form/utils/shipment-form-stop-coords';
import { focusById, focusPrimeVueInput } from '@/utils/focus';

const props = defineProps<{
  stop: ShipmentFormStopRow;
  geofences: Geofence[];
  geofencesLoading: boolean;
  placesDisabled: boolean;
  fieldErrors?: ShipmentFormStopFieldErrors;
  addressPlaceholder: string;
  locationDisabled: boolean;
  section?: 'address' | 'geofence' | 'all';
}>();

const store = useShipmentFormStore();

const googlePlaces = createGoogleMapsJsPlacesProvider({
  getApiKey: () =>
    (import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined)?.trim() ??
    '',
});

const {
  query,
  suggestions,
  loading,
  error,
  resolved,
  onInput,
  select: resolveSelection,
  clear: clearAutocomplete,
} = usePlaceAutocomplete(googlePlaces);

const geofenceSuggestions = ref<Geofence[]>([]);
const geofenceInput = ref('');

const geofenceInputClassBase =
  'w-full rounded-[6px] border border-gpx_gray-800 bg-white py-[11px] pr-10 text-sm text-gpx_black-100 placeholder:text-gpx_gray-200 focus:border-gpx_gray-400 focus:outline-none focus:ring-0 disabled:cursor-not-allowed disabled:bg-gpx_gray-900 disabled:text-gpx_gray-300';

const geofenceInputClass = computed(() =>
  props.fieldErrors?.location
    ? `${geofenceInputClassBase} !border-gpx_red-200 focus:!border-gpx_red-200`
    : geofenceInputClassBase,
);

function syncGeofenceInputFromStore() {
  const id = props.stop.geofence?.id;
  if (id == null) {
    geofenceInput.value = '';
    return;
  }
  const gf = props.geofences.find((g) => g.id === id);
  geofenceInput.value = gf?.name ?? '';
}

function onGeofenceComplete(e: { query: string }) {
  const q = e.query.trim().toLowerCase();
  geofenceSuggestions.value = !q
    ? [...props.geofences]
    : props.geofences.filter((g) => g.name.toLowerCase().includes(q));
}

function onGeofenceModelUpdate(val: string | Geofence | null | undefined) {
  if (val == null) {
    geofenceInput.value = '';
    store.setStopGeofence(props.stop.key, null);
    return;
  }
  if (typeof val === 'object' && val !== null && 'id' in val) {
    const gf = val as Geofence;
    store.setStopGeofence(props.stop.key, gf);
    clearAutocomplete();
    geofenceInput.value = gf.name;
    return;
  }
  const s = String(val);
  geofenceInput.value = s;
  const id = props.stop.geofence?.id;
  if (id != null) {
    const gf = props.geofences.find((g) => g.id === id);
    if (gf && s !== gf.name) {
      store.setStopGeofence(props.stop.key, null);
    }
  }
  if (!s.trim()) {
    store.setStopGeofence(props.stop.key, null);
  }
}

function onGeofenceClear() {
  geofenceInput.value = '';
  store.setStopGeofence(props.stop.key, null);
}

function syncAutocompleteFromStop() {
  const r = props.stop.resolved;
  if (r) {
    query.value = r.formattedAddress;
    resolved.value = r;
  } else {
    clearAutocomplete();
  }
}

onMounted(() => {
  syncGeofenceInputFromStore();
  syncAutocompleteFromStop();
});

watch(
  () => [props.stop.geofence?.id, props.geofences] as const,
  () => {
    syncGeofenceInputFromStore();
  },
);

watch(
  () => props.stop.key,
  () => {
    syncAutocompleteFromStop();
    syncGeofenceInputFromStore();
  },
);

watch(
  () => [props.stop.resolved, props.stop.geofence?.id] as const,
  () => {
    syncAutocompleteFromStop();
  },
);

function handleQueryUpdate(value: string) {
  onInput(value);
  if (!value.trim()) {
    store.setStopResolved(props.stop.key, null);
  }
}

async function handleSelect(suggestion: unknown) {
  await resolveSelection(suggestion as PlaceSuggestion);
  if (resolved.value) {
    store.setStopResolved(props.stop.key, resolved.value);
  }
}

function handleClear() {
  clearAutocomplete();
  store.setStopResolved(props.stop.key, null);
}

const coords = computed(() => getStopRoutingCoordinates(props.stop));
const latDisplay = computed(() =>
  coords.value ? coords.value.lat.toFixed(6) : '',
);
const lngDisplay = computed(() =>
  coords.value ? coords.value.lng.toFixed(6) : '',
);

function coordOrNbsp(value: string) {
  return value || '\u00A0';
}

const addressFieldErrorText = computed(
  () => props.fieldErrors?.location ?? error.value?.message ?? null,
);

const addressFieldInvalid = computed(() =>
  Boolean(props.fieldErrors?.location || error.value?.message),
);

const showAddress = computed(
  () => !props.section || props.section === 'all' || props.section === 'address',
);
const showGeofence = computed(
  () => !props.section || props.section === 'all' || props.section === 'geofence',
);

const addressAutocompleteRef = ref<{
  focusInput?: () => boolean;
  $el?: HTMLElement;
} | null>(null);

function focusAddressInput(): boolean {
  if (props.placesDisabled || props.locationDisabled) return false;
  if (addressAutocompleteRef.value?.focusInput?.()) return true;
  if (focusById(`shipment-stop-address-${props.stop.key}`)) return true;
  return focusPrimeVueInput(addressAutocompleteRef.value);
}

defineExpose({
  focusAddressInput,
});
</script>

<template>
  <div>
    <template v-if="showAddress">
      <label class="mb-1 block text-sm font-medium text-gpx_gray-100">Address *</label>
      <PlaceAutocomplete
        ref="addressAutocompleteRef"
        class="mt-0"
        :model-value="query"
        :suggestions="suggestions"
        :loading="loading"
        :error="addressFieldErrorText"
        :invalid="addressFieldInvalid"
        label=""
        :placeholder="addressPlaceholder"
        :input-id="`shipment-stop-address-${stop.key}`"
        :disabled="placesDisabled || locationDisabled"
        @update:model-value="handleQueryUpdate"
        @select="handleSelect"
        @clear="handleClear"
      />

      <div class="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2">
        <div>
          <label class="mb-1 block text-sm font-medium text-gpx_gray-100">Latitude</label>
          <div
            class="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm tabular-nums text-gpx_gray-200"
          >
            {{ coordOrNbsp(latDisplay) }}
          </div>
        </div>
        <div>
          <label class="mb-1 block text-sm font-medium text-gpx_gray-100">Longitude</label>
          <div
            class="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm tabular-nums text-gpx_gray-200"
          >
            {{ coordOrNbsp(lngDisplay) }}
          </div>
        </div>
      </div>
    </template>

    <div
      v-if="showGeofence || $slots.companion"
      class="mt-3 grid grid-cols-1 gap-3 md:grid-cols-2"
    >
      <div v-if="showGeofence">
        <label class="mb-1 block text-sm font-medium text-gpx_gray-100">Geofence</label>
      <IconField class="w-full">
        <InputIcon class="pi pi-search" />
        <AutoComplete
          class="w-full"
          :model-value="geofenceInput"
          option-label="name"
          data-key="id"
          :suggestions="geofenceSuggestions"
          :placeholder="
            geofencesLoading ? 'Loading geofences...' : 'Search geofences...'
          "
          :disabled="geofencesLoading || locationDisabled"
          :loading="geofencesLoading"
          :invalid="Boolean(fieldErrors?.location)"
          :min-length="0"
          :complete-on-focus="true"
          show-clear
          append-to="body"
          :input-class="geofenceInputClass"
          @complete="onGeofenceComplete"
          @update:model-value="onGeofenceModelUpdate"
          @clear="onGeofenceClear"
        >
          <template #option="slotProps">
            <div class="flex flex-col py-0.5 text-left">
              <span class="text-sm text-gpx_black-100">{{
                slotProps.option.name
              }}</span>
              <span
                v-if="slotProps.option.address"
                class="text-xs text-gpx_gray-200"
                >{{ slotProps.option.address }}</span
              >
            </div>
          </template>
        </AutoComplete>
      </IconField>
      </div>
      <slot name="companion" />
    </div>
  </div>
</template>
